import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, Crown, GripVertical, Pencil, Plus, Trash2 } from 'lucide-react';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Input } from 'components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { ConfirmDialog } from 'components/ui/confirm-dialog';
import { QuestionPreview, type QuestionPreviewData } from 'components/questions';
import type { QuestionTypeData } from 'components/questions/QuestionTypeEditor';
import type { QuestionType } from '@examing/types';
import type { BankDraftQuestion } from 'modules/learnArtifacts';
import { FEATURES, useFeature, useOpenUpgradeForFeature } from 'modules/entitlements';
import {
  QUESTION_TYPE_META,
  QUESTION_TYPE_ORDER,
  isAdvancedQuestionType,
} from 'pages/QuestionBank/questionTypeMeta';
import { QuestionAuthoringFields } from './QuestionAuthoringFields';

/** Fields a type switch invalidates — a reading answer key means nothing on a REORDER. */
const TYPE_SPECIFIC_KEYS = [
  'answerMode', 'acceptedAnswers', 'maxWords', 'matchingOptions', 'blanks', 'reorderItems', 'correctOrder',
  'passageText', 'passageTitle', 'subQuestions', 'rubric',
] as const;

const TYPE_DATA_KEYS = [
  'answerMode', 'acceptedAnswers', 'maxWords', 'matchingOptions', 'blanks', 'reorderItems', 'correctOrder',
  'mediaUrl', 'aiImageDescription', 'rubric', 'passageText', 'passageTitle', 'subQuestions',
] as const;

function toTypeData(question: BankDraftQuestion): QuestionTypeData {
  const out: Record<string, unknown> = {};
  for (const key of TYPE_DATA_KEYS) {
    if (question[key] !== undefined) out[key] = question[key];
  }
  return out as QuestionTypeData;
}

export interface GeneratedQuestionCardProps {
  question: BankDraftQuestion;
  index: number;
  selected: boolean;
  editing: boolean;
  /** Committed drafts are read-only: the questions are already in the bank. */
  locked: boolean;
  /**
   * Quiz drafts store plain text, two question types, and no points/difficulty.
   * difficulty or explanation. Hide those controls rather than accepting input
   * that would be dropped on save. Rich text IS supported.
   */
  limitedPayload?: boolean;
  subjectId: string;
  /**
   * Row-level flag shown beside the type badge — used by the file importer to
   * mark questions that arrived without a correct answer, so "needs attention"
   * is visible in the list rather than only after opening the editor.
   */
  notice?: React.ReactNode;
  /** Small read-only lines under the question (the file importer shows the topic it read). */
  footnote?: React.ReactNode;
  /**
   * Row actions shown on hover (always, on touch screens) beside the pencil.
   * Each is optional: a card without them — the AI review — shows none.
   */
  onAddAbove?: () => void;
  onAddBelow?: () => void;
  onDelete?: () => void;
  onToggleSelected: () => void;
  onToggleEditing: () => void;
  onChange: (patch: Partial<BankDraftQuestion>) => void;
}

/**
 * Hidden until the card is hovered or holds focus — but only where hovering
 * exists, so a phone never has actions it cannot reveal. Plain arbitrary
 * variants rather than `group-hover:`, which the web build's PostCSS drops.
 */
const REVEAL_ON_HOVER =
  '[@media(hover:hover)]:opacity-0 [.qcard:hover_&]:opacity-100 [.qcard:focus-within_&]:opacity-100';

export const GeneratedQuestionCard: React.FC<GeneratedQuestionCardProps> = ({
  question,
  index,
  selected,
  editing,
  locked,
  limitedPayload = false,
  subjectId,
  notice,
  footnote,
  onAddAbove,
  onAddBelow,
  onDelete,
  onToggleSelected,
  onToggleEditing,
  onChange,
}) => {
  const { t } = useTranslation();
  const [pendingType, setPendingType] = useState<QuestionType | null>(null);

  const canAdvancedTypes = useFeature(FEATURES.ADVANCED_QUESTION_TYPES);
  const openUpgrade = useOpenUpgradeForFeature();
  const isLockedType = (type: string) => !canAdvancedTypes && isAdvancedQuestionType(type);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: question.id,
    disabled: locked,
  });

  const meta = QUESTION_TYPE_META[question.type];
  const typeLabel = meta?.labelKey ? t(meta.labelKey) : question.type;

  /** Switching type drops the previous type's answer data, so it needs a confirm. */
  const applyTypeChange = (nextType: QuestionType) => {
    const cleared = Object.fromEntries(TYPE_SPECIFIC_KEYS.map((key) => [key, undefined]));
    onChange({
      ...cleared,
      type: nextType,
      answerMode: nextType === 'SHORT_ANSWER' ? 'exact' : undefined,
      // Choice types need option slots; everything else carries its key in the
      // type-specific fields and leaves correctAnswer empty.
      options:
        nextType === 'TRUE_FALSE'
          ? [
              { id: 'opt_true', value: 'True' },
              { id: 'opt_false', value: 'False' },
            ]
          : ['MULTIPLE_CHOICE', 'MULTIPLE_RESPONSE'].includes(nextType)
            ? question.options?.length
              ? question.options
              : [
                  { id: 'opt_0', value: '' },
                  { id: 'opt_1', value: '' },
                ]
            : undefined,
      correctAnswer: nextType === 'TRUE_FALSE' ? 'opt_true' : nextType === 'MULTIPLE_RESPONSE' ? [] : '',
    } as Partial<BankDraftQuestion>);
    setPendingType(null);
  };

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'qcard border-b border-border last:border-0',
        isDragging && 'relative z-10 rounded-md bg-card shadow-lg',
        !selected && !locked && 'opacity-55',
      )}
    >
      <div className="flex gap-3 py-3">
        {/* Include / drag rail */}
        <div className="flex shrink-0 flex-col items-center gap-1.5 pt-0.5">
          {locked ? (
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-success-600 text-white">
              <Check className="h-2.5 w-2.5" strokeWidth={3.5} />
            </span>
          ) : (
            <Checkbox
              checked={selected}
              onCheckedChange={onToggleSelected}
              aria-label={t('include_question_index', { index: index + 1 })}
            />
          )}
          {!locked && (
            <button
              type="button"
              className="cursor-grab text-muted-foreground/50 transition-colors hover:text-muted-foreground active:cursor-grabbing"
              aria-label={t('reorder')}
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="min-w-0 flex-1">
          <div className="mb-1.5 flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold tabular-nums text-muted-foreground">
              {index + 1}
            </span>
            <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
              {typeLabel}
            </span>
            {question.difficulty && (
              <span className="text-[11px] text-muted-foreground">{t(question.difficulty)}</span>
            )}
            <span className="text-[11px] text-muted-foreground">
              {t('n_points', { count: question.points })}
            </span>
            {notice}
            <span className="flex-1" />
            {!locked && (onAddAbove || onAddBelow || onDelete) && (
              <span
                className={cn('flex items-center gap-0.5 transition-opacity', REVEAL_ON_HOVER)}
              >
                {onAddAbove && (
                  <Button
                    variant="ghost"
                    className="h-6 gap-1 px-1.5 text-[11px] text-muted-foreground"
                    onClick={onAddAbove}
                    aria-label={t('import_add_above_aria', { index: index + 1 })}
                  >
                    <Plus className="h-3 w-3" />
                    {t('import_add_above')}
                  </Button>
                )}
                {onAddBelow && (
                  <Button
                    variant="ghost"
                    className="h-6 gap-1 px-1.5 text-[11px] text-muted-foreground"
                    onClick={onAddBelow}
                    aria-label={t('import_add_below_aria', { index: index + 1 })}
                  >
                    <Plus className="h-3 w-3" />
                    {t('import_add_below')}
                  </Button>
                )}
                {onDelete && (
                  <Button
                    variant="ghost"
                    className="h-6 px-1.5 text-muted-foreground hover:text-destructive"
                    onClick={onDelete}
                    aria-label={t('import_remove_question_aria', { index: index + 1 })}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </span>
            )}
            {!locked && (
              <Button
                variant={editing ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 px-2"
                onClick={onToggleEditing}
                aria-label={t('edit')}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>

          {/* Editing uses the same field rhythm, label treatment and option rows
              as the Question Bank's single-question editor. */}
          {editing && !locked ? (
            <div className="space-y-5 rounded-md border border-border bg-muted/20 p-3">
              {/* Type / difficulty / points — a quiz draft's payload has nowhere
                  to put any of these, so they're hidden rather than offered and
                  silently discarded. */}
              <div className={cn('gap-3 sm:grid-cols-3', limitedPayload ? 'hidden' : 'grid')}>
                <div className="space-y-1.5">
                  <label className="text-[13px] font-medium text-muted-foreground">
                    {t('question_type')}
                  </label>
                  <Select
                    value={question.type}
                    onValueChange={(next) => {
                      if (next === question.type) return;
                      if (isLockedType(next)) {
                        openUpgrade(FEATURES.ADVANCED_QUESTION_TYPES);
                        return;
                      }
                      setPendingType(next as QuestionType);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {QUESTION_TYPE_ORDER.map((type) => (
                        <SelectItem key={type} value={type}>
                          <span className="flex items-center gap-1.5">
                            {t(QUESTION_TYPE_META[type].labelKey)}
                            {isLockedType(type) && (
                              <Crown className="size-3 text-amber-500" aria-hidden />
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[13px] font-medium text-muted-foreground">
                    {t('difficulty')}
                  </label>
                  {/* Segmented control, matching the bank editor rather than a
                      third variation on the same choice. */}
                  <div className="inline-flex h-9 w-full items-center rounded-lg border border-border bg-muted/40 p-0.5">
                    {(['easy', 'medium', 'hard'] as const).map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => onChange({ difficulty: d })}
                        className={cn(
                          'flex-1 rounded-md px-2 py-1 text-sm font-medium transition-colors',
                          (question.difficulty ?? 'medium') === d
                            ? 'bg-card text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        {t(d === 'medium' ? 'difficulty_medium_short' : d)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[13px] font-medium text-muted-foreground">
                    {t('points')}
                  </label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={question.points}
                    onChange={(e) =>
                      onChange({ points: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })
                    }
                  />
                </div>
              </div>

              <QuestionAuthoringFields
                compact
                hideExplanation={limitedPayload}
                uploadContext={subjectId ? `question-bank/${subjectId}` : 'question-bank'}
                value={{
                  type: question.type as QuestionType,
                  prompt: question.prompt,
                  options: question.options ?? [],
                  correctAnswer: question.correctAnswer,
                  matchPairs: question.matchPairs ?? [
                    { key: '', value: '' },
                    { key: '', value: '' },
                  ],
                  explanation: question.explanation ?? '',
                  typeData: toTypeData(question),
                }}
                onChange={(patch) => {
                  const { typeData, ...rest } = patch;
                  onChange({ ...rest, ...(typeData ?? {}) } as Partial<BankDraftQuestion>);
                }}
              />

              <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={onToggleEditing}>
                  {t('done')}
                </Button>
              </div>
            </div>
          ) : (
            <QuestionPreview
              question={question as unknown as QuestionPreviewData}
              density="compact"
              showExplanation
            />
          )}
          {footnote}
        </div>
      </div>

      <ConfirmDialog
        open={pendingType !== null}
        onOpenChange={(open) => !open && setPendingType(null)}
        title={t('change_question_type')}
        description={t('change_question_type_warning')}
        confirmLabel={t('change_type')}
        cancelLabel={t('cancel')}
        onConfirm={() => pendingType && applyTypeChange(pendingType)}
      />
    </div>
  );
};

export default GeneratedQuestionCard;
